import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Image as ImageIcon,
  Loader2,
  ExternalLink,
  Package,
  Mail,
  Settings,
  LayoutDashboard,
  Instagram,
  Facebook,
  Phone,
  MapPin,
  Link as LinkIcon,
  Save,
  Star,
  Clock,
  FileText,
  ImagePlus,
  ShoppingBag,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { productsApi } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const DEFAULT_CATEGORIES = [
  { value: "bolos", label: "Bolos" },
  { value: "doces", label: "Doces" },
  { value: "salgados", label: "Salgados" },
  { value: "encomendas", label: "Encomendas" },
];

const DEFAULT_SITE = {
  whatsapp: "",
  email: "",
  city: "",
  instagram: "",
  facebook: "",
  siteText: "",
  links: "",
  ordersText: "",
  openingHours: "",
  aboutUs: "",
};

const EMPTY_PRODUCT = {
  name: "",
  category: "bolos",
  price: "",
  description: "",
  image: "",
  featured: false,
};

const STORAGE_SITE = "deco_sweet_site_settings";
const STORAGE_CATEGORIES = "deco_sweet_categories";
const STORAGE_BANNERS = "deco_sweet_banners";

function readStorage(key, fallback) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

function saveStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    toast.error("Não foi possível salvar os dados neste navegador.");
  }
}

function Field({
  label,
  icon: Icon,
  value,
  onChange,
  placeholder,
  multiline = false,
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-choco/70 mb-1.5">
        {Icon && <Icon size={14} />}
        {label}
      </label>

      {multiline ? (
        <textarea
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-choco/15 bg-white px-4 py-3 text-sm text-choco focus:outline-none focus:border-gold resize-none"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-choco/15 bg-white px-4 py-3 text-sm text-choco focus:outline-none focus:border-gold"
        />
      )}
    </div>
  );
}

function ProductModal({ open, onClose, editing, onSaved, categories }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        ...EMPTY_PRODUCT,
        ...editing,
      });
    } else {
      setForm({
        ...EMPTY_PRODUCT,
        category: categories[0]?.value || "bolos",
      });
    }
  }, [editing, open, categories]);

  if (!open) return null;

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const changeImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem válida.");
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo de 3MB.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      update("image", reader.result);
    };

    reader.onerror = () => {
      toast.error("Não foi possível carregar a imagem.");
    };

    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do produto.");
      return;
    }

    if (!form.category) {
      toast.error("Selecione uma categoria.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        price: form.price?.trim() || "",
        description: form.description?.trim() || "",
        image: form.image || "",
        featured: !!form.featured,
      };

      if (editing?.id) {
        await productsApi.update(editing.id, payload);
        toast.success("Produto atualizado com sucesso!");
      } else {
        await productsApi.create(payload);
        toast.success("Produto adicionado com sucesso!");
      }

      await onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar produto.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-cream rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-beige flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl text-choco">
              {editing ? "Editar Produto" : "Novo Produto"}
            </h2>

            <p className="text-xs text-choco/50 mt-1">
              Cadastre todas as informações do produto.
            </p>
          </div>

          <button
            onClick={onClose}
            type="button"
            className="text-choco/60 hover:text-choco"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Field
            label="Nome do produto"
            value={form.name}
            onChange={(value) => update("name", value)}
            placeholder="Ex.: Bolo de Chocolate"
          />

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-choco/70 mb-1.5 block">
                Categoria
              </label>

              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full rounded-xl border border-choco/15 bg-white px-4 py-3 text-sm text-choco focus:outline-none focus:border-gold"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <Field
              label="Preço"
              value={form.price}
              onChange={(value) => update("price", value)}
              placeholder="R$ 0,00"
            />
          </div>

          <Field
            label="Descrição"
            value={form.description}
            onChange={(value) => update("description", value)}
            placeholder="Detalhes, sabores, tamanhos..."
            multiline
          />

          <div>
            <label className="text-xs uppercase tracking-widest text-choco/70">
              Foto do produto
            </label>

            <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-28 h-28 rounded-xl overflow-hidden bg-beige flex items-center justify-center">
                {form.image ? (
                  <img
                    src={form.image}
                    alt={form.name || "Produto"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={30} className="text-choco/40" />
                )}
              </div>

              <label className="cursor-pointer rounded-full bg-choco text-cream px-5 py-2.5 text-xs uppercase tracking-widest">
                Alterar foto

                <input
                  type="file"
                  accept="image/*"
                  onChange={changeImage}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-choco cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.featured}
              onChange={(e) => update("featured", e.target.checked)}
              className="h-4 w-4"
            />

            <Star size={16} />

            <span>Destaque na página inicial</span>
          </label>
        </div>

        <div className="p-6 border-t border-beige flex justify-end gap-3">
          <button
            onClick={onClose}
            type="button"
            className="rounded-full border border-choco/20 px-6 py-2.5 text-xs uppercase tracking-widest text-choco"
          >
            Cancelar
          </button>

          <button
            onClick={save}
            type="button"
            disabled={saving}
            className="rounded-full bg-choco text-cream px-6 py-2.5 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            {saving && <Loader2 size={15} className="animate-spin" />}
            Salvar produto
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("dashboard");

  const [editing, setEditing] = useState(null);
  const [productModal, setProductModal] = useState(false);

  const [site, setSite] = useState(() =>
    readStorage(STORAGE_SITE, DEFAULT_SITE)
  );

  const [categories, setCategories] = useState(() =>
    readStorage(STORAGE_CATEGORIES, DEFAULT_CATEGORIES)
  );

  const [banners, setBanners] = useState(() =>
    readStorage(STORAGE_BANNERS, [])
  );

  const [newCategory, setNewCategory] = useState("");

  const loadProducts = useCallback(async () => {
    setLoading(true);

    try {
      const result = await productsApi.list();
      setProducts(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar produtos.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const updateSite = (field, value) => {
    setSite((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const saveSite = () => {
    saveStorage(STORAGE_SITE, site);
    toast.success("Informações do site salvas!");
  };

  const saveCategories = (items) => {
    setCategories(items);
    saveStorage(STORAGE_CATEGORIES, items);
  };

  const addCategory = () => {
    const name = newCategory.trim();

    if (!name) {
      toast.error("Digite o nome da categoria.");
      return;
    }

    const value = name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    if (!value) {
      toast.error("Nome de categoria inválido.");
      return;
    }

    if (categories.some((category) => category.value === value)) {
      toast.error("Essa categoria já existe.");
      return;
    }

    saveCategories([
      ...categories,
      {
        value,
        label: name,
      },
    ]);

    setNewCategory("");
    toast.success("Categoria adicionada!");
  };

  const removeCategory = (value) => {
    if (DEFAULT_CATEGORIES.some((category) => category.value === value)) {
      toast.error("As categorias padrão não podem ser excluídas.");
      return;
    }

    const hasProducts = products.some(
      (product) => product.category === value
    );

    if (hasProducts) {
      toast.error(
        "Não é possível excluir uma categoria que possui produtos."
      );
      return;
    }

    saveCategories(
      categories.filter((category) => category.value !== value)
    );

    toast.success("Categoria excluída.");
  };

  const addBanner = () => {
    const item = {
      id: Date.now(),
      title: "Novo banner",
      image: "",
      link: "",
      active: true,
    };

    const next = [...banners, item];

    setBanners(next);
    saveStorage(STORAGE_BANNERS, next);

    toast.success("Banner criado.");
  };

  const updateBanner = (id, field, value) => {
    const next = banners.map((banner) =>
      banner.id === id
        ? {
            ...banner,
            [field]: value,
          }
        : banner
    );

    setBanners(next);
    saveStorage(STORAGE_BANNERS, next);
  };

  const removeBanner = (id) => {
    if (!window.confirm("Excluir este banner?")) return;

    const next = banners.filter((banner) => banner.id !== id);

    setBanners(next);
    saveStorage(STORAGE_BANNERS, next);

    toast.success("Banner removido.");
  };

  const changeBannerImage = (id, e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem válida.");
      return;
    }

    if (file.size > 4 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo de 4MB.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      updateBanner(id, "image", reader.result);
      toast.success("Imagem do banner atualizada.");
    };

    reader.onerror = () => {
      toast.error("Não foi possível carregar a imagem.");
    };

    reader.readAsDataURL(file);
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Excluir "${product.name}"?`)) {
      return;
    }

    try {
      await productsApi.remove(product.id);

      toast.success("Produto excluído.");

      await loadProducts();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir produto.");
    }
  };

  const openNewProduct = () => {
    setEditing(null);
    setProductModal(true);
  };

  const openEditProduct = (product) => {
    setEditing(product);
    setProductModal(true);
  };

  const closeProductModal = () => {
    setProductModal(false);
    setEditing(null);
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const featured = products.filter((product) => product.featured);

  const menu = [
    {
      key: "dashboard",
      label: "Visão geral",
      icon: LayoutDashboard,
    },
    {
      key: "produtos",
      label: "Produtos",
      icon: Package,
    },
    {
      key: "site",
      label: "Informações do site",
      icon: Settings,
    },
    {
      key: "conteudo",
      label: "Conteúdo do site",
      icon: FileText,
    },
    {
      key: "banners",
      label: "Banners",
      icon: ImagePlus,
    },
    {
      key: "categorias",
      label: "Categorias",
      icon: ShoppingBag,
    },
  ];

  return (
    <div className="min-h-screen bg-cream text-choco">
      <header className="bg-choco text-cream">
        <div className="max-w-[1300px] mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Deco Sweet"
              className="h-11 w-auto bg-cream rounded-lg p-1"
            />

            <div>
              <p className="font-serif text-xl">
                Painel Administrativo
              </p>

              <p className="text-[10px] uppercase tracking-widest text-cream/60">
                Deco Sweet
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest"
            >
              <ExternalLink size={15} />
              Ver site
            </a>

            <button
              onClick={doLogout}
              type="button"
              className="flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2 text-xs uppercase tracking-widest"
            >
              <LogOut size={15} />
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 pt-6">
        <div className="bg-white rounded-2xl border border-beige p-2 flex gap-1 overflow-x-auto">
          {menu.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActive(item.key)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-4 py-3 text-xs uppercase tracking-widest transition ${
                  active === item.key
                    ? "bg-choco text-cream"
                    : "text-choco/70 hover:bg-beige"
                }`}
              >
                <Icon size={15} />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-[1300px] mx-auto px-5 py-8">
        {active === "dashboard" && (
          <section>
            <div className="mb-8">
              <h1 className="font-serif text-4xl">
                Visão geral
              </h1>

              <p className="text-sm text-choco/60 mt-1">
                Administração completa da Deco Sweet
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-5">
              <div className="bg-white rounded-2xl border border-beige p-6">
                <Package className="mb-4 text-gold" />

                <p className="text-3xl font-semibold">
                  {products.length}
                </p>

                <p className="text-xs uppercase tracking-widest text-choco/60 mt-1">
                  Produtos
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-beige p-6">
                <Star className="mb-4 text-gold" />

                <p className="text-3xl font-semibold">
                  {featured.length}
                </p>

                <p className="text-xs uppercase tracking-widest text-choco/60 mt-1">
                  Em destaque
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-beige p-6">
                <ShoppingBag className="mb-4 text-gold" />

                <p className="text-3xl font-semibold">
                  {categories.length}
                </p>

                <p className="text-xs uppercase tracking-widest text-choco/60 mt-1">
                  Categorias
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-beige p-6">
                <ImagePlus className="mb-4 text-gold" />

                <p className="text-3xl font-semibold">
                  {banners.length}
                </p>

                <p className="text-xs uppercase tracking-widest text-choco/60 mt-1">
                  Banners
                </p>
              </div>
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-beige p-6">
              <h2 className="font-serif text-2xl mb-5">
                Atalhos rápidos
              </h2>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openNewProduct}
                  type="button"
                  className="rounded-full bg-choco text-cream px-5 py-3 text-xs uppercase tracking-widest flex items-center gap-2"
                >
                  <Plus size={15} />
                  Novo produto
                </button>

                <button
                  onClick={() => setActive("site")}
                  type="button"
                  className="rounded-full border border-choco/20 px-5 py-3 text-xs uppercase tracking-widest"
                >
                  Editar informações
                </button>

                <button
                  onClick={() => setActive("banners")}
                  type="button"
                  className="rounded-full border border-choco/20 px-5 py-3 text-xs uppercase tracking-widest"
                >
                  Gerenciar banners
                </button>

                <button
                  onClick={() => setActive("categorias")}
                  type="button"
                  className="rounded-full border border-choco/20 px-5 py-3 text-xs uppercase tracking-widest"
                >
                  Gerenciar categorias
                </button>
              </div>
            </div>
          </section>
        )}

        {active === "produtos" && (
          <section>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-4xl">
                  Produtos
                </h1>

                <p className="text-sm text-choco/60 mt-1">
                  {products.length} cadastrado(s)
                </p>
              </div>

              <button
                onClick={openNewProduct}
                type="button"
                className="rounded-full bg-choco text-cream px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2"
              >
                <Plus size={16} />
                Novo produto
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2
                  className="animate-spin"
                  size={30}
                />
              </div>
            ) : products.length === 0 ? (
              <div className="bg-white border border-dashed border-choco/20 rounded-2xl text-center py-20">
                <Package
                  className="mx-auto mb-4 opacity-40"
                  size={40}
                />

                <p className="font-serif text-2xl">
                  Nenhum produto ainda
                </p>

                <p className="text-sm text-choco/60 mt-2">
                  Clique em “Novo produto” para cadastrar.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl border border-beige overflow-hidden"
                  >
                    <div className="aspect-video bg-beige flex items-center justify-center overflow-hidden">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon
                          size={40}
                          className="opacity-30"
                        />
                      )}
                    </div>

                    <div className="p-5">
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="bg-beige rounded-full px-2 py-1 text-[10px] uppercase tracking-widest">
                          {categories.find(
                            (category) =>
                              category.value === product.category
                          )?.label || product.category}
                        </span>

                        {product.featured && (
                          <span className="bg-gold/20 text-gold rounded-full px-2 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1">
                            <Star size={11} />
                            Destaque
                          </span>
                        )}
                      </div>

                      <h3 className="font-serif text-xl">
                        {product.name}
                      </h3>

                      <p className="text-gold font-semibold mt-1">
                        {product.price || "Sob consulta"}
                      </p>

                      <p className="text-sm text-choco/60 mt-2 line-clamp-2">
                        {product.description || "Sem descrição"}
                      </p>

                      <div className="flex gap-2 mt-5">
                        <button
                          onClick={() =>
                            openEditProduct(product)
                          }
                          type="button"
                          className="flex-1 rounded-lg border border-choco/20 py-2.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-beige"
                        >
                          <Pencil size={14} />
                          Editar
                        </button>

                        <button
                          onClick={() =>
                            deleteProduct(product)
                          }
                          type="button"
                          title="Excluir produto"
                          className="rounded-lg border border-red-200 text-red-700 px-4 hover:bg-red-50"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {active === "site" && (
          <section>
            <div className="mb-8">
              <h1 className="font-serif text-4xl">
                Informações do site
              </h1>

              <p className="text-sm text-choco/60 mt-1">
                Dados de contato e redes sociais da Deco Sweet
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-beige p-6 md:p-8 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Field
                  label="WhatsApp"
                  icon={Phone}
                  value={site.whatsapp}
                  onChange={(value) =>
                    updateSite("whatsapp", value)
                  }
                  placeholder="(11) 99999-9999"
                />

                <Field
                  label="E-mail"
                  icon={Mail}
                  value={site.email}
                  onChange={(value) =>
                    updateSite("email", value)
                  }
                  placeholder="contato@decosweet.com.br"
                />

                <Field
                  label="Cidade"
                  icon={MapPin}
                  value={site.city}
                  onChange={(value) =>
                    updateSite("city", value)
                  }
                  placeholder="São Paulo - SP"
                />

                <Field
                  label="Instagram"
                  icon={Instagram}
                  value={site.instagram}
                  onChange={(value) =>
                    updateSite("instagram", value)
                  }
                  placeholder="@decosweet"
                />

                <Field
                  label="Facebook"
                  icon={Facebook}
                  value={site.facebook}
                  onChange={(value) =>
                    updateSite("facebook", value)
                  }
                  placeholder="facebook.com/decosweet"
                />

                <Field
                  label="Links"
                  icon={LinkIcon}
                  value={site.links}
                  onChange={(value) =>
                    updateSite("links", value)
                  }
                  placeholder="Links adicionais"
                />
              </div>

              <Field
                label="Textos do site"
                icon={FileText}
                value={site.siteText}
                onChange={(value) =>
                  updateSite("siteText", value)
                }
                placeholder="Informações gerais que deseja apresentar no site..."
                multiline
              />

              <div className="flex justify-end">
                <button
                  onClick={saveSite}
                  type="button"
                  className="rounded-full bg-choco text-cream px-7 py-3 text-xs uppercase tracking-widest flex items-center gap-2"
                >
                  <Save size={15} />
                  Salvar informações
                </button>
              </div>
            </div>
          </section>
        )}

        {active === "conteudo" && (
          <section>
            <div className="mb-8">
              <h1 className="font-serif text-4xl">
                Conteúdo do site
              </h1>

              <p className="text-sm text-choco/60 mt-1">
                Edite os textos e informações da Deco Sweet
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-beige p-6 md:p-8 space-y-6">
              <Field
                label="Informações de encomendas"
                icon={ShoppingBag}
                value={site.ordersText}
                onChange={(value) =>
                  updateSite("ordersText", value)
                }
                placeholder="Como funcionam as encomendas, prazo, antecedência..."
                multiline
              />

              <Field
                label="Horários de atendimento"
                icon={Clock}
                value={site.openingHours}
                onChange={(value) =>
                  updateSite("openingHours", value)
                }
                placeholder="Segunda a sexta: 09h às 18h..."
                multiline
              />

              <Field
                label="Sobre nós"
                icon={FileText}
                value={site.aboutUs}
                onChange={(value) =>
                  updateSite("aboutUs", value)
                }
                placeholder="Conte a história da Deco Sweet..."
                multiline
              />

              <Field
                label="Textos gerais"
                icon={FileText}
                value={site.siteText}
                onChange={(value) =>
                  updateSite("siteText", value)
                }
                placeholder="Outros textos do site..."
                multiline
              />

              <div className="flex justify-end">
                <button
                  onClick={saveSite}
                  type="button"
                  className="rounded-full bg-choco text-cream px-7 py-3 text-xs uppercase tracking-widest flex items-center gap-2"
                >
                  <Save size={15} />
                  Salvar conteúdo
                </button>
              </div>
            </div>
          </section>
        )}

        {active === "banners" && (
          <section>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-4xl">
                  Banners
                </h1>

                <p className="text-sm text-choco/60 mt-1">
                  Banners da página inicial
                </p>
              </div>

              <button
                onClick={addBanner}
                type="button"
                className="rounded-full bg-choco text-cream px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2"
              >
                <Plus size={16} />
                Novo banner
              </button>
            </div>

            {banners.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-choco/20 py-20 text-center">
                <ImagePlus
                  className="mx-auto mb-4 opacity-40"
                  size={40}
                />

                <p className="font-serif text-2xl">
                  Nenhum banner cadastrado
                </p>

                <p className="text-sm text-choco/60 mt-2">
                  Clique em “Novo banner” para começar.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {banners.map((banner) => (
                  <div
                    key={banner.id}
                    className="bg-white rounded-2xl border border-beige p-5"
                  >
                    <div className="grid md:grid-cols-[220px_1fr] gap-6">
                      <div className="aspect-video bg-beige rounded-xl overflow-hidden flex items-center justify-center">
                        {banner.image ? (
                          <img
                            src={banner.image}
                            alt={banner.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImagePlus
                            size={35}
                            className="opacity-30"
                          />
                        )}
                      </div>

                      <div className="space-y-4">
                        <Field
                          label="Título"
                          value={banner.title}
                          onChange={(value) =>
                            updateBanner(
                              banner.id,
                              "title",
                              value
                            )
                          }
                          placeholder="Título do banner"
                        />

                        <Field
                          label="Link"
                          icon={LinkIcon}
                          value={banner.link}
                          onChange={(value) =>
                            updateBanner(
                              banner.id,
                              "link",
                              value
                            )
                          }
                          placeholder="https://..."
                        />

                        <div className="flex flex-wrap gap-3">
                          <label className="rounded-full bg-choco text-cream px-5 py-2.5 text-xs uppercase tracking-widest cursor-pointer">
                            Alterar imagem

                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                changeBannerImage(
                                  banner.id,
                                  e
                                )
                              }
                              className="hidden"
                            />
                          </label>

                          <button
                            onClick={() =>
                              updateBanner(
                                banner.id,
                                "active",
                                !banner.active
                              )
                            }
                            type="button"
                            className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest ${
                              banner.active
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {banner.active
                              ? "Ativo"
                              : "Inativo"}
                          </button>

                          <button
                            onClick={() =>
                              removeBanner(banner.id)
                            }
                            type="button"
                            className="rounded-full border border-red-200 text-red-700 px-5 py-2.5 text-xs uppercase tracking-widest"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {active === "categorias" && (
          <section>
            <div className="mb-8">
              <h1 className="font-serif text-4xl">
                Categorias
              </h1>

              <p className="text-sm text-choco/60 mt-1">
                Organize os produtos da Deco Sweet
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-beige p-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <input
                  value={newCategory}
                  onChange={(e) =>
                    setNewCategory(e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      addCategory();
                    }
                  }}
                  placeholder="Nome da nova categoria"
                  className="flex-1 rounded-xl border border-choco/15 px-4 py-3 text-sm focus:outline-none focus:border-gold"
                />

                <button
                  onClick={addCategory}
                  type="button"
                  className="rounded-full bg-choco text-cream px-6 py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                >
                  <Plus size={15} />
                  Adicionar
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                {categories.map((category) => (
                  <div
                    key={category.value}
                    className="rounded-xl border border-beige p-4 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-semibold">
                        {category.label}
                      </p>

                      <p className="text-xs text-choco/50 mt-1">
                        {category.value}
                      </p>
                    </div>

                    {!DEFAULT_CATEGORIES.some(
                      (item) =>
                        item.value === category.value
                    ) && (
                      <button
                        onClick={() =>
                          removeCategory(category.value)
                        }
                        type="button"
                        title="Excluir categoria"
                        className="text-red-700 hover:text-red-900"
                      >
                        <Trash2 size={17} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <ProductModal
        open={productModal}
        onClose={closeProductModal}
        editing={editing}
        onSaved={loadProducts}
        categories={categories}
      />
    </div>
  );
}
